/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { range, zip } from 'lodash';
import { transformExtent } from 'ol/proj';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerActions,
  layerSelectors,
  layerTypes,
  mapActions,
  mapSelectors,
} from '@opengeoweb/store';
import {
  LayerType,
  WMJSMAP_LONLAT_EPSGCODE,
  webmapUtils,
} from '@opengeoweb/webmap';
import type { STACGeoTIFFLayerOptions, WMLayer } from '@opengeoweb/webmap';
import {
  useGetWMLayerInstance,
  useStacCollection,
} from '@opengeoweb/webmap-react';
import LayerManagerMenuButton from './MenuButton';

interface LayerManagerMenuButtonConnectProps {
  mapId: string;
  layerId?: string;
  tooltipTitle?: string;
  icon?: React.ReactNode;
}

interface AddLayerFromCopyParams {
  mapId: string;
  oldLayerId: string;
  newLayerId: string;
}

const wmLayerHasZoomExtent = (
  wmLayer: WMLayer | null,
  mapSrs: string,
): boolean => {
  if (!wmLayer) {
    return false;
  }

  const crs = wmLayer.getCRSByName(mapSrs);
  if (crs?.bbox) {
    return true;
  }

  return !!wmLayer.geographicBoundingBox;
};

const LayerManagerMenuButtonConnect: React.FC<
  LayerManagerMenuButtonConnectProps
> = ({
  layerId,
  mapId,
  tooltipTitle,
  icon,
}: LayerManagerMenuButtonConnectProps) => {
  const dispatch = useDispatch();

  const layer = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerById(store, layerId),
  );

  const layerType = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerType(store, layerId),
  );
  const isFeatureSyncLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getIsFeatureSyncLayer(store, layerId),
  );

  const isFeatureLayer = layerType === LayerType.featureLayer;
  const isStacGeoTIFFLayer = layerType === LayerType.stacGeoTIFFLayer;

  const { data: stacCollectionData } = useStacCollection(
    isStacGeoTIFFLayer ? (layer?.service ?? '') : '',
  );
  const isTiledStacCollection = Boolean(stacCollectionData?.item_assets);

  const stacOptions =
    layer?.options && !('parameterNames' in layer.options)
      ? layer.options
      : undefined;

  const mapSrs = useSelector((store: CoreAppStore) =>
    mapSelectors.getSrs(store, mapId),
  );

  const { wmLayer } = useGetWMLayerInstance(layer);

  const canZoomToLayerExtent = !!(
    layerId &&
    mapSrs &&
    wmLayerHasZoomExtent(wmLayer, mapSrs)
  );

  // get all map ids
  const allMapIds = useSelector(mapSelectors.getAllMapIds);
  const allOtherMapIdsExceptCurrentOne = allMapIds.filter((id) => id !== mapId);
  const allLayers = useSelector(layerSelectors.getAllLayers);

  const copyLayerToOtherMaps = React.useCallback(() => {
    // generate new layer ids
    const newLayerIds = range(allOtherMapIdsExceptCurrentOne.length).map((_) =>
      webmapUtils.generateLayerId(),
    );
    // run all duplicateLayer calls in a loop
    zip(allOtherMapIdsExceptCurrentOne, newLayerIds).forEach(
      ([newMapId, newLayerId]) => {
        if (!newMapId || !layerId || !newLayerId) {
          return;
        }
        dispatch(
          layerActions.duplicateMapLayer({
            mapId: newMapId,
            oldLayerId: layerId,
            newLayerId,
            origin: layerTypes.LayerActionOrigin.layerManager,
          }),
        );
      },
    );
  }, [allOtherMapIdsExceptCurrentOne, dispatch, layerId]);

  const removeLayerFromAllMaps = React.useCallback(() => {
    if (!layerId) {
      return;
    }
    const layerName = allLayers.find((layer) => layer.id === layerId)?.name;
    layerName &&
      allLayers
        .filter((layer) => layer.name === layerName && layer.mapId !== mapId)
        .forEach((layer) => {
          layer.mapId &&
            layer.id &&
            dispatch(
              layerActions.layerDelete({
                mapId: layer.mapId,
                layerId: layer.id,
              }),
            );
        });
  }, [dispatch, allLayers, mapId, layerId]);

  const zoomToLayerExtent = React.useCallback(() => {
    if (!layerId || !mapSrs || !wmLayer) {
      return;
    }

    const crs = wmLayer.getCRSByName(mapSrs);
    if (crs?.bbox) {
      const [left, bottom, right, top] = crs.bbox.getExtent();
      dispatch(
        mapActions.setBbox({
          mapId,
          srs: mapSrs,
          bbox: { left, bottom, right, top },
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      );
      return;
    }

    const geo = wmLayer.geographicBoundingBox;
    if (geo) {
      const [left, bottom, right, top] = transformExtent(
        [
          parseFloat(geo.west),
          parseFloat(geo.south),
          parseFloat(geo.east),
          parseFloat(geo.north),
        ],
        WMJSMAP_LONLAT_EPSGCODE,
        mapSrs,
      );

      dispatch(
        mapActions.setBbox({
          mapId,
          srs: mapSrs,
          bbox: { left, bottom, right, top },
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      );
    }
  }, [dispatch, layerId, mapId, mapSrs, wmLayer]);

  const duplicateLayer = React.useCallback(
    ({ newLayerId, mapId }: AddLayerFromCopyParams) =>
      dispatch(
        layerActions.duplicateMapLayer({
          mapId,
          oldLayerId: layerId!,
          newLayerId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, layerId],
  );

  const changeStacDataLayout = React.useCallback(
    (dataLayout: NonNullable<STACGeoTIFFLayerOptions['dataLayout']>): void => {
      if (!layerId) {
        return;
      }

      dispatch(
        layerActions.layerChangeOptions({
          layerId,
          mapId,
          options: {
            ...stacOptions,
            dataLayout,
          },
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      );
    },
    [dispatch, layerId, mapId, stacOptions],
  );

  return (
    <LayerManagerMenuButton
      canZoomToLayerExtent={canZoomToLayerExtent}
      icon={icon}
      isFeatureLayer={isFeatureLayer}
      isFeatureSyncLayer={isFeatureSyncLayer}
      layerId={layerId}
      mapId={mapId}
      isTiledStacCollection={
        isStacGeoTIFFLayer ? isTiledStacCollection : undefined
      }
      onChangeStacDataLayout={
        isStacGeoTIFFLayer ? changeStacDataLayout : undefined
      }
      onLayerCopyToOtherMaps={copyLayerToOtherMaps}
      onLayerDuplicate={(): void => {
        duplicateLayer({
          newLayerId: webmapUtils.generateLayerId(),
          mapId,
        } as AddLayerFromCopyParams);
      }}
      onLayerRemoveFromOtherMaps={removeLayerFromAllMaps}
      onLayerZoomToExtent={zoomToLayerExtent}
      stacDataLayout={stacOptions?.dataLayout}
      tooltipTitle={tooltipTitle}
    />
  );
};

export default LayerManagerMenuButtonConnect;
